/**
 * usePublicProposal - Hook da landing pública de propostas (/p/:token)
 *
 * A página pública nunca conversa com o banco: toda comunicação passa pela
 * Edge Function `public-proposal`, que detém a service_role e resolve o token.
 *
 * @version 1.1.0
 * RELEASE: 2026-08-11 v1.1.0 - Medição do tempo ativo na página (pausa quando a aba
 *          fica oculta) enviada de forma throttled (30s) e no encerramento via
 *          fetch keepalive. O backend consolida no próprio evento da sessão.
 * RELEASE: 2026-08-11 v1.0.0 - Criação inicial (Fase 6 do módulo de Propostas)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('usePublicProposal');

/** Item (snapshot congelado) exibido na landing */
export interface PublicProposalItem {
  product_name: string;
  product_type: string | null;
  unit_type: string | null;
  quantity: number;
  unit_price: number;
  discount_percent: number | null;
  discount_fixed: number | null;
  subtotal: number;
  total: number;
  notes: string | null;
  recurring_price: number | null;
  recurrence_interval: string | null;
}

/**
 * Payload devolvido quando o link foi REVOGADO (proposta substituída/cancelada).
 * Não traz itens, valores nem PDF — apenas o suficiente para orientar o cliente.
 */
export interface PublicProposalRevoked {
  revoked: true;
  revoked_reason: 'superseded' | 'cancelled' | 'disabled';
  proposal: { proposal_number: string; version_number: number; status: string };
  tenant: { name: string | null; logo_url: string | null };
  successor: {
    proposal_number: string;
    version_number: number;
    /** Token da versão mais recente (apenas quando o link dela está ativo) */
    token: string | null;
  } | null;
}

/** Payload devolvido pela Edge Function na action 'resolve' */
export interface PublicProposalData {
  /** Presente somente no payload de link revogado */
  revoked?: boolean;
  revoked_reason?: PublicProposalRevoked['revoked_reason'];
  successor?: PublicProposalRevoked['successor'];
  proposal: {
    id: string;
    proposal_number: string;
    version_number: number;
    title: string | null;
    description: string | null;
    conditions_html: string | null;
    status: string;
    currency: string;
    subtotal: number;
    discount_total: number;
    total: number;
    mrr_total: number;
    valid_from: string | null;
    expires_at: string | null;
    accepted_at: string | null;
    accepted_by_name: string | null;
    rejected_at: string | null;
    pdf_status: string;
    is_actionable: boolean;
  };
  tenant: { name: string | null; logo_url: string | null };
  deal: { title: string | null; client_name: string | null };
  items: PublicProposalItem[];
  pdf_url: string | null;
  /**
   * Modelo do Studio (opcional). Quando ausente, a página usa o modelo padrão,
   * que reproduz exatamente o layout atual.
   */
  studio_document?: unknown;
  /** HTML compilado do modelo, quando o modelo for do tipo HTML bruto. */
  studio_html?: string | null;
}


/** Gera/recupera um id de sessão anônimo (dedupe de visualizações) */
function getSessionId(): string {
  const key = 'uz_public_proposal_sid';
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const generated = crypto.randomUUID();
    sessionStorage.setItem(key, generated);
    return generated;
  } catch {
    return 'anonymous';
  }
}

export function usePublicProposal(token: string | undefined) {
  const [data, setData] = useState<PublicProposalData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sessionId = useMemo(() => getSessionId(), []);

  /** Chamada genérica à Edge Function pública */
  const call = useCallback(
    async (action: string, extra: Record<string, unknown> = {}) => {
      const { data: response, error: fnError } = await supabase.functions.invoke(
        'public-proposal',
        { body: { action, token, session_id: sessionId, ...extra } },
      );
      if (fnError) throw new Error(fnError.message);
      if (!response?.success) throw new Error(response?.error || 'Não foi possível concluir.');
      return response.data;
    },
    [token, sessionId],
  );

  /** Resolve a proposta pelo token */
  const load = useCallback(async () => {
    if (!token) {
      setError('Link inválido.');
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await call('resolve');
      setData(result as PublicProposalData);
    } catch (err: any) {
      logger.error('Falha ao resolver proposta pública', { error: err?.message });
      setError(err?.message || 'Proposta não encontrada.');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [call, token]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Aceite da proposta pelo cliente */
  const accept = useCallback(
    async (actorName: string, actorEmail?: string) => {
      setIsSubmitting(true);
      try {
        await call('accept', {
          actor_name: actorName,
          actor_email: actorEmail || null,
          idempotency_key: `${sessionId}-accept`,
        });
        await load();
      } finally {
        setIsSubmitting(false);
      }
    },
    [call, load, sessionId],
  );

  /** Recusa da proposta pelo cliente */
  const reject = useCallback(
    async (actorName: string, reason?: string, actorEmail?: string) => {
      setIsSubmitting(true);
      try {
        await call('reject', {
          actor_name: actorName,
          actor_email: actorEmail || null,
          reason: reason || null,
          idempotency_key: `${sessionId}-reject`,
        });
        await load();
      } finally {
        setIsSubmitting(false);
      }
    },
    [call, load, sessionId],
  );

  // ---------------------------------------------------------------------------
  // Medição do tempo ativo na página (v1.1.0)
  // ---------------------------------------------------------------------------
  const activeSecondsRef = useRef(0);
  const lastSentRef = useRef(0);
  const isVisibleRef = useRef(true);

  /** Envia o total acumulado; usa keepalive para funcionar no unload */
  const sendDuration = useCallback(
    (useKeepalive: boolean) => {
      const total = Math.round(activeSecondsRef.current);
      if (!token || total <= 0 || total === lastSentRef.current) return;
      lastSentRef.current = total;

      const body = JSON.stringify({
        action: 'log_event',
        token,
        session_id: sessionId,
        event_type: 'opened',
        duration_seconds: total,
      });

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/public-proposal`;
      const apiKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      void fetch(url, {
        method: 'POST',
        keepalive: useKeepalive,
        headers: {
          'Content-Type': 'application/json',
          apikey: apiKey,
          Authorization: `Bearer ${apiKey}`,
        },
        body,
      }).catch((err) => logger.warn('Falha ao registrar permanência', { error: err?.message }));
    },
    [token, sessionId],
  );

  useEffect(() => {
    if (!token) return;

    // Conta apenas segundos com a aba visível (tempo ativo real)
    const tick = window.setInterval(() => {
      if (!isVisibleRef.current) return;
      activeSecondsRef.current += 1;
      if (activeSecondsRef.current - lastSentRef.current >= 30) sendDuration(false);
    }, 1000);

    const handleVisibility = () => {
      isVisibleRef.current = document.visibilityState === 'visible';
      if (!isVisibleRef.current) sendDuration(true);
    };
    const handlePageHide = () => sendDuration(true);

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.clearInterval(tick);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pagehide', handlePageHide);
      sendDuration(true);
    };
  }, [token, sendDuration]);

  /** Registra o download do PDF (tracking) */
  const trackPdfDownload = useCallback(async () => {
    try {
      await call('log_event', { event_type: 'downloaded_pdf' });
    } catch (err: any) {
      logger.warn('Falha ao registrar download do PDF', { error: err?.message });
    }
  }, [call]);

  return { data, isLoading, error, isSubmitting, reload: load, accept, reject, trackPdfDownload };
}
